import type { SelectOptionItem } from '@/types/common/models/SelectOption.types'

import { toTitleCase } from '@/utils/casing-util/casingUtil'

/**
 * Converts a record object to an array of SelectOptionItem objects.
 *
 * @template T - Type extending Record with string keys and values compatible with SelectOptionItem['value']
 * @param {T} input - The record object to convert
 * @returns {SelectOptionItem[]} An array of SelectOptionItem objects
 */
const toSelectOptionFormat = <T extends Record<string, SelectOptionItem['value']>>(input: T): SelectOptionItem[] =>
  Object.entries(input).map(([key, value]) => ({ label: key, value }))

/**
 * Creates an array of SelectOptionItem objects from the input, optionally adding unknown options.
 *
 * @template T - Type extending Record with string keys and values compatible with SelectOptionItem['value']
 * @param {T | SelectOptionItem[]} input - Either a record object or an array of SelectOptionItem objects
 * @param {string[]} [unknowns=[]] - Array of unknown labels to add as options if they don't already exist
 * @returns {SelectOptionItem[]} Combined array of unknown options followed by base options
 *
 * @example
 * // Using with a record object
 * const options = selectOption({ 'Option 1': 'value1', 'Option 2': 'value2' });
 * // Result: [{ label: 'Option 1', value: 'value1' }, { label: 'Option 2', value: 'value2' }]
 *
 * @example
 * // Using with an array of SelectOptionItem objects
 * const options = selectOption([
 *   { label: 'Option 1', value: 'value1' },
 *   { label: 'Option 2', value: 'value2' }
 * ]);
 * // Result: Same array is returned
 *
 * @example
 * // Using with unknown options
 * const options = selectOption(
 *   { 'Option 1': 'value1', 'Option 2': 'value2' },
 *   ['Option 3']
 * );
 * // Result: [
 * //   { label: 'Option 3', value: 'Option 3', disabled: false },
 * //   { label: 'Option 1', value: 'value1' },
 * //   { label: 'Option 2', value: 'value2' }
 * // ]
 */
const selectOption = <T extends Record<string, SelectOptionItem['value']>>(
  input: T | SelectOptionItem[],
  unknowns: string[] = []
): SelectOptionItem[] => {
  const baseOptions = Array.isArray(input) ? input : toSelectOptionFormat(input)

  const baseLabels = new Set(baseOptions.map(option => option.label))

  const unknownOptions: SelectOptionItem[] = unknowns
    .filter(label => !baseLabels.has(label))
    .map(label => ({
      label,
      value: label,
      disabled: false
    }))

  return [...unknownOptions, ...baseOptions]
}

/**
 * Converts a constant object to select options with formatted labels using title case
 *
 * @template T - Type extending Record with string keys and string values
 * @param {T} constants - An object with constant values
 * @returns {SelectOptionItem[]} An array of select options with formatted labels and constant values
 *
 * @example
 * const STATUS = { PENDING: 'PENDING', COMPLETED: 'COMPLETED' } as const
 * convertConstToSelectOptions(STATUS)
 * // Returns: [{ label: 'Pending', value: 'PENDING' }, { label: 'Completed', value: 'COMPLETED' }]
 */
const convertConstToSelectOptions = <T extends Record<string, string>>(constants: T): SelectOptionItem[] => {
  return Object.values(constants).map(value => ({
    label: toTitleCase(value),
    value: value
  }))
}

/**
 * Converts a label map, keyed by stored value, to select options. The map's order is the options' order.
 *
 * @example
 * labelsToSelectOptions({ draft: 'Draft', sent: 'Sent' })
 * // Returns: [{ label: 'Draft', value: 'draft' }, { label: 'Sent', value: 'sent' }]
 */
const labelsToSelectOptions = (labels: Readonly<Record<string, string>>): SelectOptionItem[] =>
  Object.entries(labels).map(([value, label]) => ({ label, value }))

/**
 * Converts a named entity to a select option, with its id as the value. Pass it straight to `map`.
 *
 * @example
 * suppliers.map(entityToSelectOption)
 * // Returns: [{ label: 'Acme', value: 7 }, ...]
 */
const entityToSelectOption = ({ id, name }: { id: string | number; name: string }): SelectOptionItem => ({
  label: name,
  value: id
})

/**
 * Merges selected options into search results, deduplicating by value.
 * Selected items appear first so PrimeVue resolves labels/chips immediately.
 * Used by search-backed select/autocomplete components to display pre-selected
 * values that may not appear in paginated search results.
 *
 * @param searchOptions - Options from the search/query results
 * @param selectedOptions - Known selected options (from props, cache, or selection tracker)
 * @returns Merged array with selected items first, then non-duplicate search results
 */
const mergeWithSelected = (
  searchOptions: SelectOptionItem[],
  selectedOptions: SelectOptionItem[]
): SelectOptionItem[] => {
  if (selectedOptions.length === 0) return searchOptions

  const selectedKeys = new Set(selectedOptions.map(o => String(o.value)))

  return [...selectedOptions, ...searchOptions.filter(o => !selectedKeys.has(String(o.value)))]
}

export { selectOption, convertConstToSelectOptions, labelsToSelectOptions, entityToSelectOption, mergeWithSelected }
